import { useEffect, PropsWithChildren } from 'react';
import {
  ThemeProvider,
  StyledEngineProvider,
  useTheme,
} from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import { useMediaQuery } from '@mui/material';
import Container from '@mui/material/Container';
import { useSettings } from '../../hooks';
import { styled } from '@mui/material';
import { Icon } from '@iconify/react';
import { IconButton } from '@mui/material';
import menu2Fill from '@iconify/icons-eva/menu-2-fill';
import { setDocumentTitle, setDocumentFavicon } from '../../util';

// types and classes
import type { $TSFixMe } from '@calefy-inc/utilityTypes';
import { PolicyStreamDefaultFooter } from './components/DefaultPolicyStreamFooter';
import { PolicyStreamSaveQuoteButton } from './components/PolicyStreamSaveQuoteButton';

const useStyle = () => {
  const styles = makeStyles((theme: $TSFixMe) => ({
    container: {
      flex: 1,
      minHeight: '60vh',
      padding: theme.spacing(2),
    },
    speedDial: {
      position: 'absolute',
      bottom: theme.spacing(4),
      right: theme.spacing(4),
    },
  }));
  return styles();
};

interface PolicyStreamWhiteLabelProps extends PropsWithChildren<{}> {
  onOpenSidebar?: () => void;
}
/**
 * A Component which renders a quote wizard with specific theme and settings
 * @param  children - child that is wrapped by this component
 * @returns Quote Wizard
 */
export const PolicyStreamWhitelabel = ({
  children,
  onOpenSidebar,
}: PolicyStreamWhiteLabelProps) => {
  const {
    ready,
    settings: { companyTheme, logo, headerLogo, title, favicon, ...settings },
  } = useSettings();
  const headerLogoToUse = headerLogo ?? logo;
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('md'));
  const classes = useStyle();

  useEffect(() => {
    if (!ready) return;
    setDocumentTitle(title);
    if (!favicon) return;
    setDocumentFavicon(favicon);
  }, [ready, title, favicon]);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={companyTheme}>
        <WhiteLabelContainer>
          <StyledHeader
            sx={settings.headerCss ? settings.headerCss : undefined}
          >
            {!ready ? null : matches ? (
              <>
                <MobileHeader>
                  {headerLogoToUse ? (
                    <img src={headerLogoToUse} alt='Logo' />
                  ) : null}
                  <IconButton
                    onClick={onOpenSidebar}
                    sx={{
                      mr: 1,
                      color: 'text.primary',
                      position: 'sticky',
                    }}
                    size='large'
                  >
                    <Icon icon={menu2Fill} />
                  </IconButton>
                </MobileHeader>

                <MobileSaveContainer>
                  <PolicyStreamSaveQuoteButton />
                </MobileSaveContainer>
              </>
            ) : (
              <>
                {headerLogoToUse ? (
                  <img src={headerLogoToUse} alt='Logo' />
                ) : null}
                <PolicyStreamSaveQuoteButton />
              </>
            )}
          </StyledHeader>
          <Container className={classes.container}>
            {ready ? children : null}
          </Container>
          {!ready ? null : settings.Footer ? (
            <settings.Footer />
          ) : (
            <PolicyStreamDefaultFooter />
          )}
        </WhiteLabelContainer>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

const MobileHeader = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
});

const MobileSaveContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  paddingTop: theme.spacing(1),
}));

const WhiteLabelContainer = styled('div')({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  overflowX: 'hidden',
});

const StyledHeader = styled('header')(({ theme }) => {
  return {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: 0,
    padding: theme.spacing(2),
    '& img': {
      maxWidth: theme.spacing(14),
      maxHeight: theme.spacing(14),
      alignSelf: 'center',
      [theme.breakpoints.down('xl')]: {
        maxWidth: theme.spacing(12),
        maxHeight: theme.spacing(12),
      },
    },
    '& button': {
      alignSelf: 'center',
    },
    [theme.breakpoints.down('xl')]: {
      margin: 0,
    },
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column', // Stack items vertically on mobile
      justifyContent: 'flex-start',
      alignItems: 'stretch', // Allow children to stretch full width
      gap: theme.spacing(1), // Add space between rows
    },
  };
});
